export type HomeLookbookTileSize = 'hero' | 'standard';

export type HomeLookbookItem = {
  id: string;
  src: string;
  fallbackSrc: string;
  alt: string;
  size: HomeLookbookTileSize;
  tag: string;
  caption: string;
  handle: string;
  productName: string;
  productSlug?: string;
  shopTag?: string;
  /** Resolved PDP or safe shop fallback — never a fictional slug path. */
  href?: string;
  saveCount?: number;
  objectPosition?: string;
};

/** Curated community-style lookbook — order drives hero + rail layout. */
export const HOME_LOOKBOOK_ITEMS: readonly HomeLookbookItem[] = [
  {
    id: 'layered-fleece',
    src: 'https://i.pinimg.com/736x/c0/a7/be/c0a7be3d7ee7d6ee51f7ac3880e5e5e5.jpg',
    fallbackSrc: '/assets/t-shirt-white-mockup.png',
    alt: 'Layered sweatshirt street fit',
    size: 'hero',
    tag: 'Layered fit',
    caption: 'Soft fleece · elevated basics',
    handle: '@yassine.fit',
    productName: 'Abstract Dreams sweatshirt',
    productSlug: 'abstract-dreams',
    saveCount: 128,
    objectPosition: 'center 30%',
  },
  {
    id: 'minimal-tee',
    src: 'https://i.pinimg.com/736x/f4/8f/11/f48f118359871b62d44c0bafe9cf4961.jpg',
    fallbackSrc: '/assets/t-shirt-black-mockup.png',
    alt: 'Minimal chest graphic tee',
    size: 'standard',
    tag: 'Minimal',
    caption: 'Clean type · premium cotton',
    handle: '@sara.casablanca',
    productName: 'Studio Monogram tee',
    productSlug: 'studio-monogram',
    saveCount: 84,
    objectPosition: 'center 22%',
  },
  {
    id: 'street-hoodie',
    src: 'https://i.pinimg.com/736x/90/ba/2b/90ba2b569a6cf0d67d76a2af13f574b8.jpg',
    fallbackSrc: '/assets/tshirts/white-tshirt-front.png',
    alt: 'Oversized hoodie mood',
    size: 'standard',
    tag: 'Streetwear',
    caption: 'Heavyweight hood · bold color',
    handle: '@med.urban',
    productName: 'Retro Wave hoodie',
    productSlug: 'retro-wave',
    saveCount: 96,
    objectPosition: 'center 35%',
  },
  {
    id: 'mountain-graphic',
    src: 'https://i.pinimg.com/736x/68/fe/f8/68fef885052e09d6a6446269f8b55404.jpg',
    fallbackSrc: '/assets/t-shirt-workspace.png',
    alt: 'Graphic tee outdoor shot',
    size: 'standard',
    tag: 'Graphic',
    caption: 'Mountain print · everyday wear',
    handle: '@amina.outdoor',
    productName: 'Minimalist Mountain tee',
    productSlug: 'minimalist-mountain',
    saveCount: 71,
    objectPosition: 'center 40%',
  },
  {
    id: 'city-nights',
    src: 'https://i.pinimg.com/736x/bd/bc/89/bdbc893332d71b330f571ce631f8b766.jpg',
    fallbackSrc: '/assets/tshirts/white-tshirt-back.png',
    alt: 'Wide lifestyle outfit shot',
    size: 'standard',
    tag: 'City nights',
    caption: 'Urban palette · unisex cut',
    handle: '@karim.night',
    productName: 'Urban Vibes tee',
    productSlug: 'urban-vibes',
    saveCount: 112,
    objectPosition: 'center 45%',
  },
  {
    id: 'art-sweat',
    src: 'https://i.pinimg.com/736x/b3/6e/6c/b36e6c00c6d8c5a3ab167c4266808450.jpg',
    fallbackSrc: '/assets/tshirts/black-tshirt-front.png',
    alt: 'Art-led sweatshirt detail',
    size: 'standard',
    tag: 'Art drop',
    caption: 'Statement front · limited run',
    handle: '@lina.studio',
    productName: 'Night Serif hoodie',
    productSlug: 'night-serif',
    saveCount: 63,
    objectPosition: 'center 28%',
  },
];

export const HOME_LOOKBOOK_FEATURE = HOME_LOOKBOOK_ITEMS[0];

export const HOME_LOOKBOOK_RAIL = HOME_LOOKBOOK_ITEMS.filter((item) => item.size === 'standard');

function getLookbookById(id: string): HomeLookbookItem | undefined {
  return HOME_LOOKBOOK_ITEMS.find((item) => item.id === id);
}

/** Mobile hero — Streetwear (custom primary image). */
const streetHoodie = getLookbookById('street-hoodie');
export const HOME_LOOKBOOK_MOBILE_FEATURE: HomeLookbookItem = streetHoodie
  ? {
      ...streetHoodie,
      src: 'https://i.pinimg.com/736x/29/1c/35/291c352e3a5d6a6faf15c31a9c90bcd8.jpg',
      alt: 'Streetwear duo in black graphic tees',
      objectPosition: 'center 32%',
    }
  : HOME_LOOKBOOK_FEATURE;

/** Mobile filmstrip order: Art drop → City nights → rest. */
const HOME_LOOKBOOK_MOBILE_RAIL_ORDER = [
  'art-sweat',
  'city-nights',
  'minimal-tee',
  'mountain-graphic',
  'layered-fleece',
] as const;

export const HOME_LOOKBOOK_MOBILE_RAIL = HOME_LOOKBOOK_MOBILE_RAIL_ORDER.map((id) =>
  getLookbookById(id),
).filter((item): item is HomeLookbookItem => item !== undefined);

/** Desktop hero — Art drop (left feature on large screens). */
export const HOME_LOOKBOOK_DESKTOP_FEATURE =
  HOME_LOOKBOOK_ITEMS.find((item) => item.id === 'art-sweat') ?? HOME_LOOKBOOK_FEATURE;

export const HOME_LOOKBOOK_DESKTOP_RAIL = HOME_LOOKBOOK_ITEMS.filter(
  (item) => item.id !== HOME_LOOKBOOK_DESKTOP_FEATURE.id,
);

/** Safe shop navigation — inspiration tiles never deep-link to fictional listing slugs. */
export function getLookbookHref(item: HomeLookbookItem): string {
  if (item.href?.trim()) return item.href.trim();
  if (item.shopTag?.trim()) {
    return `/shop?tag=${encodeURIComponent(item.shopTag.trim())}`;
  }
  if (item.tag?.trim()) {
    return `/shop?tag=${encodeURIComponent(item.tag.trim().toLowerCase())}`;
  }
  return '/shop';
}
